/**
 * 월드 탭 순수 데이터 빌더 (Phaser-free) — 월드 목록과 잠금/클리어/현재 상태를 파생한다.
 *
 * - currentWorld: currentChapter로 파생.
 * - cleared/unlocked: bestChapter가 해당 월드 시작 챕터 이상이면 클리어 or 해금.
 * - 이동 가능: 클리어된 월드이고 현재 월드가 아닐 때만.
 */
import type { WorldDefinition } from "../data/worldData.js";
import {
  WORLD_DEFS,
  WORLD_CHAPTER_SPAN,
  resolveWorldForChapter,
  chapterForWorld,
  chapterInWorld,
} from "../data/worldData.js";

export type WorldCellStatus = "current" | "cleared" | "locked";

export interface WorldTabCell {
  readonly worldId: string;
  readonly worldNo: number;
  readonly nameKey: string;
  readonly iconKey: string;
  readonly status: WorldCellStatus;
  /** 해당 월드 내 진행 챕터 (cleared면 span, current면 현재 위치, locked면 0). */
  readonly localChapter: number;
  /** 해당 월드의 챕터 span. */
  readonly chapterSpan: number;
  /** 이동 가능 여부 (cleared && !current). */
  readonly canMove: boolean;
}

export function buildWorldTabCells(
  currentChapter: number,
  bestChapter: number,
  chapterSpan: number = WORLD_CHAPTER_SPAN,
  worlds: readonly WorldDefinition[] = WORLD_DEFS,
): readonly WorldTabCell[] {
  const currentWorld = resolveWorldForChapter(currentChapter, chapterSpan, worlds);
  return worlds.map((world) => {
    const worldStartChapter = chapterForWorld(world.worldNo, chapterSpan, worlds);
    const clearThresholdChapter = worldStartChapter + chapterSpan;
    const cleared = bestChapter >= clearThresholdChapter;
    const isCurrent = world.worldNo === currentWorld.worldNo;

    let status: WorldCellStatus;
    if (isCurrent) {
      status = "current";
    } else if (cleared) {
      status = "cleared";
    } else {
      status = "locked";
    }

    let localChapter: number;
    if (status === "locked") {
      localChapter = 0;
    } else if (status === "cleared") {
      localChapter = chapterSpan;
    } else {
      // current
      localChapter = chapterInWorld(currentChapter, chapterSpan);
    }

    return {
      worldId: world.worldId,
      worldNo: world.worldNo,
      nameKey: `world.${world.worldId}.name`,
      iconKey: world.iconKey,
      status,
      localChapter,
      chapterSpan,
      canMove: status === "cleared" && !isCurrent,
    };
  });
}
